import React, { useEffect, useState } from "react";
import { apiClient } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Loader2, Plus, Pencil, Trash2, RotateCcw } from "lucide-react";
import { toast } from "sonner";

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await apiClient.get("/admin/users");
      setUsers(r.data || []);
    } catch {
      toast.error("Erro ao listar usuários.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleDelete = async (u) => {
    if (!window.confirm(`Remover ${u.email}? Esta ação não pode ser desfeita.`)) return;
    try {
      await apiClient.delete(`/admin/users/${u.id}`);
      toast.success("Usuário removido.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao remover.");
    }
  };

  const handleResetPwd = async (u) => {
    try {
      await apiClient.patch(`/admin/users/${u.id}`, { reset_password: true });
      toast.success("Bloqueio limpo. Solicitada troca no próximo login.");
      load();
    } catch {
      toast.error("Falha ao redefinir.");
    }
  };

  return (
    <div className="px-6 md:px-12 py-8 max-w-[1400px] mx-auto" data-testid="admin-users-page">
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground mb-2">
            Administração
          </div>
          <h1 className="font-display text-4xl font-bold tracking-tight leading-none">
            Usuários
          </h1>
          <p className="text-sm text-muted-foreground mt-2">
            Crie usuários, atribua MFA fixo e gerencie permissões. Não há autocadastro.
          </p>
        </div>
        <Button onClick={() => setShowNew(true)} data-testid="new-user-button">
          <Plus className="w-4 h-4 mr-2" /> Novo usuário
        </Button>
      </div>

      <div className="bg-card border rounded-md overflow-x-auto" data-testid="admin-users-table">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Nome</TableHead>
              <TableHead>E-mail</TableHead>
              <TableHead>Papel</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Último acesso</TableHead>
              <TableHead className="text-right">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-10 text-muted-foreground">
                  <Loader2 className="w-4 h-4 animate-spin inline-block mr-2" />
                  Carregando…
                </TableCell>
              </TableRow>
            ) : (
              users.map((u) => (
                <TableRow key={u.id} data-testid={`user-row-${u.email}`}>
                  <TableCell className="font-medium">{u.full_name}</TableCell>
                  <TableCell>{u.email}</TableCell>
                  <TableCell>
                    <Badge variant={u.role === "admin" ? "default" : "outline"}>{u.role}</Badge>
                  </TableCell>
                  <TableCell>
                    {u.is_active ? (
                      <Badge variant="outline" className="text-buy border-buy">Ativo</Badge>
                    ) : (
                      <Badge variant="outline" className="text-sell border-sell">Inativo</Badge>
                    )}
                    {u.must_change_password && (
                      <Badge variant="secondary" className="ml-1 text-[10px]">trocar senha</Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {u.last_login_at ? new Date(u.last_login_at).toLocaleString("pt-BR") : "—"}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleResetPwd(u)}
                      data-testid={`reset-user-${u.email}`}
                    >
                      <RotateCcw className="w-3.5 h-3.5" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setEditing(u)}
                      data-testid={`edit-user-${u.email}`}
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(u)}
                      data-testid={`delete-user-${u.email}`}
                    >
                      <Trash2 className="w-3.5 h-3.5 text-destructive" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <UserDialog
        open={showNew}
        onClose={() => setShowNew(false)}
        onSaved={() => {
          setShowNew(false);
          load();
        }}
      />
      <UserDialog
        open={!!editing}
        editing={editing}
        onClose={() => setEditing(null)}
        onSaved={() => {
          setEditing(null);
          load();
        }}
      />
    </div>
  );
}

function UserDialog({ open, onClose, onSaved, editing }) {
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    password: "",
    mfa_code: "",
    role: "user",
    is_active: true,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (editing) {
      setForm({
        full_name: editing.full_name,
        email: editing.email,
        password: "",
        mfa_code: "",
        role: editing.role,
        is_active: editing.is_active,
      });
    } else {
      setForm({
        full_name: "",
        email: "",
        password: "",
        mfa_code: "",
        role: "user",
        is_active: true,
      });
    }
    setError("");
  }, [editing, open]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (editing) {
        const payload = {
          full_name: form.full_name,
          role: form.role,
          is_active: form.is_active,
        };
        if (form.password) payload.new_password = form.password;
        if (form.mfa_code) payload.mfa_code = form.mfa_code;
        await apiClient.patch(`/admin/users/${editing.id}`, payload);
      } else {
        await apiClient.post("/admin/users", form);
      }
      toast.success(editing ? "Usuário atualizado." : "Usuário criado.");
      onSaved();
    } catch (err) {
      setError(err?.response?.data?.detail || "Erro ao salvar.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="user-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">
            {editing ? "Editar usuário" : "Novo usuário"}
          </DialogTitle>
          <DialogDescription>
            {editing
              ? "Atualize informações; deixe senha e MFA em branco para preservar."
              : "Defina uma senha temporária e o código MFA de 6 dígitos para o novo usuário."}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label>Nome completo</Label>
            <Input
              required
              value={form.full_name}
              onChange={(e) => setForm({ ...form, full_name: e.target.value })}
              data-testid="user-form-name"
            />
          </div>
          <div className="space-y-2">
            <Label>E-mail</Label>
            <Input
              required
              type="email"
              disabled={!!editing}
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              data-testid="user-form-email"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>{editing ? "Nova senha (opcional)" : "Senha temporária"}</Label>
              <Input
                type="password"
                required={!editing}
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                data-testid="user-form-password"
              />
            </div>
            <div className="space-y-2">
              <Label>MFA (6 dígitos)</Label>
              <Input
                inputMode="numeric"
                pattern="[0-9]{6}"
                maxLength={6}
                required={!editing}
                value={form.mfa_code}
                onChange={(e) => setForm({ ...form, mfa_code: e.target.value.replace(/\D/g, "") })}
                className="font-mono-tab tracking-[0.3em]"
                data-testid="user-form-mfa"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>Papel</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="user-form-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="user">Usuário</SelectItem>
                  <SelectItem value="admin">Administrador</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Status</Label>
              <Select
                value={form.is_active ? "true" : "false"}
                onValueChange={(v) => setForm({ ...form, is_active: v === "true" })}
              >
                <SelectTrigger data-testid="user-form-status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="true">Ativo</SelectItem>
                  <SelectItem value="false">Inativo</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {error && (
            <div className="text-sm text-destructive bg-destructive/10 border border-destructive/30 rounded-md px-3 py-2">
              {error}
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose}>
              Cancelar
            </Button>
            <Button type="submit" disabled={loading} data-testid="user-form-submit">
              {loading ? "Salvando…" : "Salvar"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
